import React, { useEffect, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import CustomerRow from '../smallComponent/CustomerRow';
import TotalSummary from '../payment/TotalSummary';
import CustomerFilterBar from '../smallComponent/CustomerFilterBar';
import { useCustomers } from '../context/CustomerContext';

export default function TotalCustomer() {
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);

  const { allCustomers, fetchCustomers, loading } = useCustomers();
  const pageSize = 30;

  // 🔹 Filter whenever customers or filter inputs change
  useEffect(() => {
    let result = allCustomers || [];

    // search filter
    if (search) {
      result = result.filter((c) =>
        (c.customer_name || "")
          .toLowerCase()
          .includes(search.toLowerCase())
      );
    }

    // date filter
    if (startDate) {
      result = result.filter(
        (c) => new Date(c.done_date) >= new Date(startDate)
      );
    }
    if (endDate) {
      result = result.filter(
        (c) => new Date(c.done_date) <= new Date(endDate)
      );
    }

    // sirf active customers
    result = result.filter(
      (c) => c.status === 1 || c.status === 2 || c.status === 3
    );

    setFilteredCustomers(result);
    setCurrentPage(1);
  }, [allCustomers, search, startDate, endDate]);

  // 🔹 Reset
  const resetFilter = () => {
    setSearch("");
    setStartDate("");
    setEndDate("");
    setFilteredCustomers(allCustomers || []);
    setCurrentPage(1);
  };

  // 🔹 Export
  const exportToExcel = () => {
    const data = filteredCustomers.map((c, i) => ({
      SN: i + 1,
      Name: c.customer_name || '',
      Phone: c.contact_number || '',
      Business: c.business_name || '',
      StartDate: c.project_start_date
        ? new Date(c.project_start_date).toLocaleDateString()
        : '',
      DoneDate: c.done_date ? new Date(c.done_date).toLocaleDateString() : '',
      Developer: c.developer_name || '',
      Salesman: c.salesman_name || '',
      Price: c.price || 0,
      Due: c.due_amount || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');
    XLSX.writeFile(workbook, 'CustomerList.xlsx');
  };

  // 🔹 Summary
  const totalAmount = filteredCustomers.reduce(
    (acc, c) => acc + (c.price || 0),
    0
  );
  const totalDue = filteredCustomers.reduce(
    (acc, c) => acc + (c.due_amount || 0),
    0
  );
  const totalReceived = totalAmount - totalDue;

  // 🔹 Pagination
  const totalPages = Math.ceil(filteredCustomers.length / pageSize);
  const indexOfLast = currentPage * pageSize;
  const indexOfFirst = indexOfLast - pageSize;
  const currentCustomers = showAll
    ? filteredCustomers
    : filteredCustomers.slice(indexOfFirst, indexOfLast);

  if (loading) return <p className="text-center py-4">Loading customers...</p>;

  return (
    <div className="container mt-4">

      {/* 🔹 Filter bar */}
      <Card className="mb-3 shadow-sm">
        <Card.Body>
          <CustomerFilterBar
            search={search}
            setSearch={setSearch}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            onExport={exportToExcel}
            onReset={resetFilter}
          />
        </Card.Body>
      </Card>

      {/* 🔹 Customer Table */}
      <div className="overflow-x-auto bg-white rounded shadow">
        <table className="min-w-full border-collapse border border-gray-200 table-auto">
          <thead className="bg-gray-800 text-white">
            <tr>
              <th>SN</th>
              <th>Customer Info</th>
              <th>Start Date</th>
              <th>Sales / Developer</th>
              <th>Work Details</th>
              <th>Payment</th>
              <th>Call</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentCustomers.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-3 text-muted">
                  No customers found
                </td>
              </tr>
            ) : (
              currentCustomers.map((customer, idx) => (
                <CustomerRow
                  key={customer.id}
                  customer={customer}
                  index={showAll ? idx : indexOfFirst + idx}
                  refresh={fetchCustomers}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 🔹 Pagination + Show All */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          {showAll
            ? `Showing all ${filteredCustomers.length} customers`
            : `Page ${currentPage} of ${totalPages}`}
        </div>
        <div className="d-flex gap-2">
          {!showAll && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                ◀ Prev
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Next ▶
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant={showAll ? 'warning' : 'info'}
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? '🔁 Paginate' : '📄 Show All'}
          </Button>
        </div>
      </div>

      {/* 🔹 Summary */}
      <TotalSummary
        customers={filteredCustomers}
        totalAmount={totalAmount}
        totalDue={totalDue}
        totalReceived={totalReceived}
      />
    </div>
  );
}
